import {selectUserId} from '../../state/selectors/auth';
import {getState} from '../../state/store';
import indexedDBService from '../indexedDBService';
import loyaltyService from './loyaltyService';

const LOYALTY_DB_NAME = 'LoyaltyDB';
const LOYALTY_DB_STORE = 'LoyaltyStore';
const KEY_PATH = 'id';

let INDEX_DB_QUEUE = [];
const inflight = new Set();

let isProcessing = false;
let initialQueueLength = 0;
const processingQueue = [];

const processQueue = async () => {
    if (isProcessing || processingQueue.length === 0) return;

    isProcessing = true;

    if (initialQueueLength === 0) {
        initialQueueLength = processingQueue.length;
    }

    while (processingQueue.length > 0) {
        const {key, isRetry, ...item} = processingQueue.shift();

        try {
            if (isRetry) {
                const userId = getUserId();

                await loyaltyService.sendBadgeData({
                    userId,
                    isRetry: true,
                    isSilent: item.isSilent,
                    badgeType: item.badgeType,
                    milestone: item.milestoneType,
                });
            } else {
                await addMilestoneToQueue(item);
            }
        } catch (err) {
            console.error('Error processing queue item:', err);
        } finally {
            inflight.delete(key);
        }
    }

    isProcessing = false;

    if (initialQueueLength > 0 && INDEX_DB_QUEUE.length === 0) {
        await loyaltyService.getBadgeData();
    }

    initialQueueLength = 0;
};

const getUserId = () => selectUserId(getState());

const getBadgesForCurrentUser = async () => {
    const userId = getUserId();
    const all = await indexedDBService.getDatabaseData(LOYALTY_DB_NAME, LOYALTY_DB_STORE, KEY_PATH);

    return (all || []).filter((item) => item.userId === userId);
};

const setBadgeToIndexDB = async (data) => {
    const userId = getUserId();
    const record = {
        ...data,
        userId,
        id: `${userId}:${data.milestone}`,
    };

    await indexedDBService.putDatabaseItem(LOYALTY_DB_NAME, LOYALTY_DB_STORE, KEY_PATH, record);
    await updateBadgeQueue();
};

const deleteBadgeFromIndexDB = async (id) => {
    await indexedDBService.deleteDatabaseItemById(LOYALTY_DB_NAME, LOYALTY_DB_STORE, KEY_PATH, id);
    await updateBadgeQueue();
};

const updateBadgeQueue = async (uId = getUserId()) => {
    INDEX_DB_QUEUE = uId ? await getBadgesForCurrentUser() : [];
};

const initQueue = async () => {
    const userId = getUserId();

    if (!userId) return;

    await updateBadgeQueue(userId);

    INDEX_DB_QUEUE.forEach(({milestone, badge, isSilent}) => {
        const key = `${userId}:${badge}/${milestone}`;

        if (inflight.has(key)) return;

        inflight.add(key);

        processingQueue.push({
            key,
            isSilent,
            isRetry: true,
            badgeType: badge,
            milestoneType: milestone,
        });
    });

    processQueue();
};

const addMilestoneToQueue = async ({milestoneType, badgeType, isSilent}) => {
    const userId = getUserId();

    if (!userId) return;

    const exists = INDEX_DB_QUEUE.some(({milestone}) => milestone === milestoneType);

    if (exists) return;

    const data = {milestone: milestoneType, badge: badgeType, isSilent};

    await setBadgeToIndexDB(data);
    await loyaltyService.sendBadgeData({userId, badgeType, milestone: data.milestone, isSilent});
};

const deleteBadgeFromQueue = async (badgeType) => {
    const idx = INDEX_DB_QUEUE.findIndex(({badge}) => badge === badgeType);

    if (idx === -1) return;

    await deleteBadgeFromIndexDB(INDEX_DB_QUEUE[idx].id);
};

const getQueue = () => INDEX_DB_QUEUE;

const addMilestoneToQueueSafe = async ({badgeType, milestoneType, isSilent, isRetry = false}) => {
    const key = `${getUserId()}:${badgeType}/${milestoneType}`;

    if (inflight.has(key)) return;

    inflight.add(key);

    processingQueue.push({badgeType, milestoneType, isSilent, key, isRetry});

    processQueue();
};

export default {
    initQueue,
    addMilestoneToQueue,
    addMilestoneToQueueSafe,
    deleteBadgeFromQueue,
    getQueue,
};
